import {Page} from "../page";

export const gameKits: Page = {
    uri: 'learn/game-kits',
    icon: '🧩',
    title: $localize`:@@page-game-kits-title:Game Kits`,
    seoTitle: $localize`:@@page-game-kits-seo-title:Board game kits — setup checklists and score sheets that total themselves`,
    summary: $localize`:@@page-game-kits-summary:Community-built play aids for any game — structured score sheets that total themselves, setup checklists, round steps, roles and randomizers.`,
    markdownContent: [
        $localize`:@@page-game-kits-what-s-in-a-kit:Game Kits are gameplay companions created by the community. They walk you through setup, track round steps, organize scoring, and manage special roles — all tailored to a specific board game. Using a Kit during play keeps everyone on the same page and makes complex games much easier to run.

## What's in a Kit

Every Kit contains some or all of these components:

### Setup Steps

![Setup steps checklist with sections and checkmarks](/assets/screenshots/game-kits-setup-steps.webp)

An ordered checklist to prepare your game. Steps are grouped into sections (e.g., "Components," "Player Setup," "Game Board"), with descriptions and optional images for each. You work through them in order when you create a [live session](/learn/live-sessions).

### Round Steps
A recurring checklist for what happens between turns or rounds — things like "Resolve player actions," "Check for end-game condition," or "Collect resources." You reference this throughout play.

### Score Sheet

![Score sheet with categories, per-player values, and auto-totals](/assets/screenshots/game-kits-score-sheet.webp)

The scoring template. It has rows for each scoring category (e.g., "Gold," "VP from Settlements," "Leader Bonus") grouped into logical sections. You fill in per-player values, and the app auto-totals. Some Kits support per-round scoring for games with incremental points.

### Roles and Colors
Character types, factions, or player colors with optional images. Useful for games where players choose roles at the start. When a player's role isn't on the kit's list (a class unlocked partway through a campaign, which kits leave out so they never spoil it), pick **Other…** in the role field and type it.

![A published kit: configuration, roles with portraits, colors, and its score sheet](/assets/screenshots/game-kits-kit-page.webp)

### Randomizers
Attach a randomizer to any setup step to make those fiddly setup draws instant — dealing out factions, picking a map layout, choosing which modules are in play. You list the options (or reuse the kit's roles), decide how many get drawn, and whether repeats are allowed.

Two touches worth knowing: the number drawn can change with player count, and options can carry **states** — so a tile can come up "side B" or "rotated 90°" as well as simply being chosen.

Not everything should be random. A randomizer can instead let the table **choose** its options, for scenarios, missions or maps people pick on purpose.`,

        $localize`:@@page-game-kits-expansion-and-module-support:## Expansion and Module Support

Board games evolve. Kits handle this by tagging content to specific expansions or modules:

- **Setup steps, round steps, and score rows** can all be tagged to expansions
- **When you start a session**, you select which expansions are active
- **Only relevant content shows** — expansions you didn't select are hidden
- **Replacement content** swaps base-game items when an expansion is active. For example, a new scoring category might replace a base-game one

This keeps the Kit clean and focused on what you're actually playing.`,

        $localize`:@@page-game-kits-player-count-awareness:## Player Count Awareness

Setup and round steps, score rows and whole score categories can be tagged with the player counts they apply to: exact counts, a range, "3 or more" or "2 or fewer", up to the game's own maximum. Ludoya then shows only what matters for the number of players you've actually got — so the two-player setup variant appears when you're playing two, and stays out of the way when you're playing four.

A kit can also say which player counts it's made for. Pick it for a table outside that range and the picker warns you, though you can still use it.

Randomizers can vary by player count too: the same randomizer can draw three cards at four players and five at six.`,

        $localize`:@@page-game-kits-score-row-types:## What a Score Row Asks For

Each score row says what players fill in, under **Players fill in** in the row's editor:

- **A number** — the points, typed in. Give it **points per item** (4 per ruby) and players type how many instead: the sheet works out the points and shows them in the cell. An item can cost points too (-3 per wound).
- **A tick** — yes or no, like longest road. Give it **points for a tick** and a ticked box scores them.
- **One option of a list** — players pick one, like a character's level. Each option can score its own points, and renaming or reordering the options later leaves old plays pointing at the option they picked.
- **What's left of a start** — an end value out of a start, like 2 of 3 lives. Set the usual start on the row; at the table, tap "of 3" beside the row's name to change it for that game (an easier start of 6). These never add to the total: the same number means a different game from a different start.

A row can also be:

- **A win condition** — meeting it wins the game outright, whatever the totals say. Usually a tick; tick **Players type which one** for games won by one of several numbered conditions (mission cards), and players type the mission's number instead. Any number but 0 is a win, and the play remembers which one it was.
- **A tiebreaker** — asked only when the top totals tie, and only of the tied players. Choose whether the higher or the lower value wins the tie.
- **Not added to the total** — a count the game keeps but doesn't score, shown in its place on the sheet.
- **Just for stats** — something you want to record for your stats, not part of the game's score (rounds played, cards left). It never counts towards the total and sits apart, under its own **Stats** heading below the result.`,

        $localize`:@@page-game-kits-using-a-kit-during-play:## Using a Kit During Play

### Select When Logging a Play
When you log or start a play, its Kit sits right under the game, as a **Kit** field with the Kit's name (and its author, for a community Kit). If you've set a default Kit for that game, it's picked for you. Tap the field to see the game's Kits: each says who wrote it, how many plays used it and what it holds (setup steps, rounds, a score sheet and its categories), your default first, then the most used, with a search box once there are a few. Pick one and it's on the play at once; **No kit**, at the top, plays without one, for this play only. The **Always start … with this kit** switch makes the picked Kit your default for the game, and **ⓘ** opens a Kit to read it first.

### Work Through Setup Steps
The app guides you step-by-step. Check off each item as you complete it. Skip steps if they don't apply to your session.

### Use Round Checklist Between Turns
Refer to the round steps anytime you need a reminder of what happens next in the turn structure.

### Fill in Score Sheet
Enter scores directly into the Kit's score sheet. The app totals everything for you.

### Real-Time Sync
If multiple players are using the app during the session, everyone sees updates instantly. Setup progresses, scores update, and no one falls out of sync.`,

        $localize`:@@page-game-kits-finding-kits:## Finding Kits

### On the Game Page
Open any game and find the **Game Kits** section. The **Ludoya kit**, the one Ludoya writes from the rulebook and anyone can improve, comes first, then the rest with your language first and by popularity, and each one previews what you're getting: how many people use it, and how many roles, setup steps, round steps and score rows it contains. Private kits carry a lock.

### On a Creator's Profile
Visit someone's [profile](/learn/user-profiles) to browse every Kit they've made.

### Picking One Automatically
Set a kit as your **default** for a game and it's chosen for you every time you play it, on every device you sign in on: turn on the **Always start … with this kit** switch where you pick a play's kit, or tick **Default for this game** on the game's kits.

Without a default, Ludoya picks the game's **Ludoya kit** when it has one. Otherwise it only auto-selects when there's a clear winner — a kit in your own language that's both the most used and the best rated. If nothing stands out, it leaves the choice to you rather than guessing.

### In Your Language
Kits show in the language you use Ludoya in. A kit written in another language is translated for you, so kit lists don't show languages; the kit's details say which one it was written in. Spotted a clumsy translation? **Improve translation** on the kit's details shows the original next to the translation and takes your fix. Copying or exporting a translated kit gives you a kit in that language. The kit's details also list everyone who contributed to it.

### Improving the Ludoya Kit
The game's Ludoya kit is written from the rulebook, and anyone can make it better. Open its details and tap **Improve**: it opens in the kit editor you already know. Change what's wrong, say what you're improving if you like, and send it. Someone from Ludoya checks your edit against the rulebook before it goes live, and you get a notification either way. **History** on the kit's details lists every edit that went live, plus your own still waiting or not accepted, and lets you undo your own live edits. Images can't be changed this way.`,

        $localize`:@@page-game-kits-creating-a-kit:## Creating a Kit

Anyone can create a Kit for free. The process is simple:

### Multi-Step Editor

![Game Kit editor showing the multi-step creation flow](/assets/screenshots/game-kits-editor.webp)

1. **General** — Name and language (required)
2. **[Campaign](/learn/campaigns)** (optional) — Define world or character fields for multi-session campaigns
3. **Setup** — Add checklist items grouped into sections
4. **Rounds** — Define recurring turn-structure steps
5. **Scoring** — Build the score sheet with categories and rows

### Pro Mode

![Pro Mode toggle revealing expansion and module management](/assets/screenshots/game-kits-pro-mode.webp)

Toggle Pro Mode to reveal:
- **Modules** — variants and mini-expansions you can tag content to
- **Roles and colors** — factions, characters, player colours
- **Campaign support** — the multi-session fields described below
- **Drag-to-reorder** for steps and score rows
- **Total calculation** — when a score sheet has several categories, choose whether the total is the sum of all rows, the highest category, or the lowest

You don't need Pro Mode to make a useful Kit — plain kits work great for most games.

### Only Name and Language Are Required
Everything else is optional, but there's more available than you might expect: mark the game **cooperative**, have the kit **choose a starting player**, turn scoring off entirely for games that don't have any, set a **round count**, score **per round**, give players **starting points**, or flag that **lower is winner** for golf-style scoring. Individual score rows can be marked as a **win condition**, or tied to a module, an expansion, or another row they replace.

### Keeping a Kit to Yourself
Tick **Don't share this kit** and it stays private — it won't appear in the game's kit list or the community feed, and only you can see and use it. Perfect for house rules, or for a kit you're still building.`,

        $localize`:@@page-game-kits-copying-a-kit:## Copying a Kit

Found a Kit you like but want to change something? Choose **Copy Game Kit**. You get your own editable duplicate, and the original is untouched — the polite way to build on someone else's work.`,

        $localize`:@@page-game-kits-campaign-fields:## Campaign Fields

For games that span multiple sessions (legacy games, RPGs, [campaigns](/learn/campaigns)), Game Kits can define special fields:

### Global Fields
Describe the world state — things like "Current Day," "Kingdom Stability," "Threat Level." All players see these.

### Character Fields
Per-player state — like "HP," "Experience Points," "Inventory," "Character Class." Each player has their own values.

### Supported Field Types
- **Text** — Open-ended notes or descriptions
- **Number** — Quantities, scores, health
- **Toggle** — Yes/No states (e.g., "Has Magic Stone")
- **Selection** — Dropdown choices (e.g., Class = Fighter/Mage/Rogue)
- **Image** — Upload a photo or portrait
- **Role** — Link to a character or faction defined in the Kit
- **Color** — Assign a color`,

        $localize`:@@page-game-kits-import-and-export:## Import and Export

Exchange Kits as JSON files — export yours to share or back up, import someone else's to use as your own.

**This one is web only.** The mobile apps have no file picker for it, so if someone sends you a \`.json\` Kit you'll need a browser to bring it in. Sharing Kits *between Ludoya users* doesn't need any of this: open the Kit's page and tap **Copy**, which works everywhere.`,

        $localize`:@@page-game-kits-social-features:## Social Features

**Like** a Kit to show you use and love it, and **comment** with tips or questions — open a kit to find both. Community feedback is how creators know what to improve.

Want your own version? **Copy** any kit and edit it freely, or **export** it to move it elsewhere.`,

        $localize`:@@page-game-kits-related-pages:## Related Pages

- [Live Sessions](/learn/live-sessions) — Use a Kit when running a live game
- [Play Logging](/learn/play-logging) — Game Kits appear when you log a play
- [Campaigns](/learn/campaigns) — Game Kits power structured campaign tracking
- [Collection](/learn/collection) — Kits are listed on game detail pages in your collection`,
    ].join('\n\n---\n\n'),
};
